import { useLazyGetUsageDataCSVQuery } from "queries/telemetryApi";
import React, { PropsWithChildren, useContext, useMemo, useState } from "react";
import { useAppSelector } from "reduxUtils/hook";
import {
  selectBillingEntityDataEndAtOrToday,
  selectBillingEntityDataStartAtOrToday,
} from "selectors/billingEntitySelector";
import { jwtClient } from "utils/jwtClient";
import { sendAnalyticsEvent } from "utils/sendAnalyticsEvent";

import { downloadCsv } from "./csvFileDownload";
import {
  validateDate,
  validateDateRange,
} from "./validateCsvDownloadDates.utils";

interface CsvDownloaderContextProps {
  startDate?: FormField;
  endDate?: FormField;
  handleClick?: (event: React.FormEvent<HTMLFormElement>) => void;
  isDownloading?: boolean;
}
const initialContext: CsvDownloaderContextProps = {};

const CsvDownloaderContext = React.createContext(initialContext);

const initialFormFieldState = {
  error: "",
  isRequired: true,
  isValid: false,
  value: "",
};

export const useCsvDownloaderContext = () => useContext(CsvDownloaderContext);

export const CsvDownloaderContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [startDate, setStartDate] = useState(initialFormFieldState);
  const [endDate, setEndDate] = useState(initialFormFieldState);
  const [isDownloading, setIsDownloading] = useState(false);

  const { icpNumber } = useAppSelector((store) => store.currentAccount);

  const billingEntityDataStartAtOrToday = useAppSelector(
    selectBillingEntityDataStartAtOrToday,
  );
  const billingEntityDataEndAtOrToday = useAppSelector(
    selectBillingEntityDataEndAtOrToday,
  );

  const [trigger] = useLazyGetUsageDataCSVQuery();

  const validateStartOrEndDate = useMemo(
    () => (dateValue: string, dateType: "start" | "end") => {
      return validateDate({
        dateValue: dateValue,
        minDate: billingEntityDataStartAtOrToday,
        maxDate: billingEntityDataEndAtOrToday,
        fieldType: dateType,
      });
    },
    [billingEntityDataStartAtOrToday, billingEntityDataEndAtOrToday],
  );

  const setDateValueOnChange = (startValue: string, endValue: string) => {
    setStartDate((prev) => {
      const error = prev.error
        ? validateStartOrEndDate(startValue, "start")
        : ""; // revalidate if there was an error
      return {
        ...prev,
        value: startValue,
        error,
        isValid: !!startValue && !error,
      };
    });

    setEndDate((prev) => {
      const error = prev.error ? validateStartOrEndDate(endValue, "end") : ""; // revalidate if there was an error
      return {
        ...prev,
        value: endValue,
        error,
        isValid: !!endValue && !error,
      };
    });
  };

  const setDateValueOnBlur = (startValue: string, endValue: string) => {
    const startError = validateStartOrEndDate(startValue, "start");
    const endError = validateStartOrEndDate(endValue, "end");
    const rangeError = validateDateRange(startValue, endValue);

    setStartDate((prev) => ({
      ...prev,
      error: startError,
      isValid: !!startDate.value && !startError,
    }));
    setEndDate((prev) => ({
      ...prev,
      error: endError || rangeError,
      isValid: !!endDate.value && !(endError || rangeError),
    }));
  };

  const handleClick = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setIsDownloading(true);
    try {
      sendAnalyticsEvent("download_usage_csv", {
        icpNumber,
        csv_start_date: startDate.value,
        csv_end_date: endDate.value,
      });

      const response = await trigger({
        jwtClient,
        icpNumber,
        startAt: startDate.value,
        endAt: endDate.value,
      });

      downloadCsv(response.data, `${startDate.value}_${endDate.value}.csv`);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <CsvDownloaderContext.Provider
      value={{
        startDate: {
          fieldValue: startDate,
          handleOnChange: (value) => setDateValueOnChange(value, endDate.value),
          handleOnBlur: () =>
            setDateValueOnBlur(startDate.value, endDate.value),
        },
        endDate: {
          fieldValue: endDate,
          handleOnChange: (value) =>
            setDateValueOnChange(startDate.value, value),
          handleOnBlur: () =>
            setDateValueOnBlur(startDate.value, endDate.value),
        },
        handleClick,
        isDownloading,
      }}
    >
      {children}
    </CsvDownloaderContext.Provider>
  );
};
